import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

// Admin-only cross-store Prebuilt Agent publisher. Browse ANY store's workflows
// and publish selected ones to all stores or specific stores — without switching
// the current store. Also lists currently-published templates with Unpublish.
// All endpoints are under /api/cognitive-flows/admin/* and require role === 'admin'.

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  modal: { background: 'var(--surface,#fff)', borderRadius: '14px', width: '100%', maxWidth: '640px', maxHeight: '88vh', boxShadow: '0 12px 40px rgba(0,0,0,0.22)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  // Inline (embedded) variant — rendered as a page section (e.g. the Admin
  // Dashboard "Prebuilt Agents" tab) instead of a fixed overlay.
  embedded: { background: 'var(--surface,#fff)', borderRadius: '14px', width: '100%', maxWidth: '760px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  title: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', lineHeight: 1, color: '#9ca3af', padding: '4px', borderRadius: '6px' },
  body: { padding: '20px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '5px', display: 'block' },
  select: { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', marginBottom: '14px' },
  radioRow: { display: 'flex', gap: '18px', marginBottom: '12px', fontSize: '13px' },
  storeList: { maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 10px', marginBottom: '14px' },
  storeItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', color: 'var(--text-primary)' },
  btnPrimary: (disabled) => ({ padding: '8px 18px', borderRadius: '8px', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: '13px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }),
  card: { border: '1px solid var(--border)', borderRadius: '10px', padding: '20px 22px', background: '#fbfcfe' },
  pubRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border)' },
  pubName: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' },
  pubMeta: { fontSize: '11px', color: 'var(--text-muted)' },
  unpubBtn: { padding: '5px 12px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: '12px', cursor: 'pointer', flexShrink: 0 },
  empty: { fontSize: '13px', color: 'var(--text-muted)', padding: '8px 0' },
  divider: { border: 'none', borderTop: '1px solid var(--border)', margin: 0 },
};

export default function PublishManagerModal({ onClose, embedded = false }) {
  const [stores, setStores] = useState([]);
  const [sourceShop, setSourceShop] = useState('');
  const [sourceWorkflows, setSourceWorkflows] = useState([]);
  const [loadingWf, setLoadingWf] = useState(false);
  const [selectedWfId, setSelectedWfId] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [targetShops, setTargetShops] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState([]);

  const nameOf = useCallback((key) => stores.find((st) => st.shopKey === key)?.name || key, [stores]);

  const loadPublished = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/published');
      setPublished(data);
    } catch {
      // non-fatal; the panel still lets you publish
    }
  }, []);

  useEffect(() => {
    api.get('/admin/stores').then(({ data }) => setStores(data)).catch(() => toast.error('Failed to load stores'));
    loadPublished();
  }, [loadPublished]);

  useEffect(() => {
    if (!sourceShop) { setSourceWorkflows([]); setSelectedWfId(''); return; }
    let cancelled = false;
    setLoadingWf(true);
    api.get(`/admin/workflows?shop=${encodeURIComponent(sourceShop)}`)
      .then(({ data }) => { if (!cancelled) { setSourceWorkflows(data); setSelectedWfId(''); } })
      .catch(() => { if (!cancelled) toast.error('Failed to load workflows'); })
      .finally(() => { if (!cancelled) setLoadingWf(false); });
    return () => { cancelled = true; };
  }, [sourceShop]);

  const toggleTarget = (key) => {
    setTargetShops((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const publish = async () => {
    if (!selectedWfId) { toast.error('Pick a workflow to publish'); return; }
    if (visibility === 'stores' && targetShops.length === 0) { toast.error('Select at least one target store'); return; }
    setPublishing(true);
    const tid = toast.loading('Publishing…');
    try {
      await api.post(`/admin/workflows/${selectedWfId}/publish`, {
        visibility,
        shops: visibility === 'stores' ? targetShops : [],
      });
      toast.success('Published', { id: tid });
      setSelectedWfId('');
      setTargetShops([]);
      await loadPublished();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to publish', { id: tid });
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async (id) => {
    const tid = toast.loading('Unpublishing…');
    try {
      await api.post(`/admin/workflows/${id}/unpublish`);
      toast.success('Unpublished', { id: tid });
      await loadPublished();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unpublish', { id: tid });
    }
  };

  const inner = (
    <>
        <div style={s.header}>
          <span style={s.title}>Manage Prebuilt Agents</span>
          {!embedded && <button type="button" className="btn-height" style={s.closeBtn} onClick={onClose} title="Close">✕</button>}
        </div>

        <div style={s.body}>
          <div style={s.card}>
            <div style={s.sectionTitle}>Publish an agent</div>

            <span style={s.label}>Source store</span>
            <select style={s.select} value={sourceShop} onChange={(e) => setSourceShop(e.target.value)}>
              <option value="">Select a store…</option>
              {stores.map((st) => <option key={st.shopKey} value={st.shopKey}>{st.name}</option>)}
            </select>

            {sourceShop && (
              <>
                <span style={s.label}>Workflow</span>
                <select style={s.select} value={selectedWfId} onChange={(e) => setSelectedWfId(e.target.value)} disabled={loadingWf}>
                  <option value="">{loadingWf ? 'Loading…' : 'Select a workflow…'}</option>
                  {sourceWorkflows.map((w) => <option key={w._id} value={w._id}>{w.name}</option>)}
                </select>

                <span style={s.label}>Show it to</span>
                <div style={s.radioRow}>
                  <label><input type="radio" name="vis" checked={visibility === 'all'} onChange={() => setVisibility('all')} /> All stores</label>
                  <label><input type="radio" name="vis" checked={visibility === 'stores'} onChange={() => setVisibility('stores')} /> Specific stores</label>
                </div>

                {visibility === 'stores' && (
                  <div style={s.storeList}>
                    {stores.map((st) => (
                      <label key={st.shopKey} style={s.storeItem}>
                        <input type="checkbox" checked={targetShops.includes(st.shopKey)} onChange={() => toggleTarget(st.shopKey)} />
                        {st.name}
                      </label>
                    ))}
                  </div>
                )}

                <button type="button" className="btn-height" style={s.btnPrimary(publishing || !selectedWfId)} disabled={publishing || !selectedWfId} onClick={publish}>
                  {publishing ? 'Publishing…' : 'Publish'}
                </button>
              </>
            )}
          </div>

          <div>
            <div style={s.sectionTitle}>Published agents</div>
            {published.length === 0 ? (
              <p style={s.empty}>Nothing published yet.</p>
            ) : (
              published.map((p) => (
                <div key={p._id} style={s.pubRow}>
                  <div>
                    <div style={s.pubName}>{p.name}</div>
                    <div style={s.pubMeta}>
                      from {nameOf(p.shop)} · {p.publishVisibility === 'all'
                        ? 'All stores'
                        : `${(p.publishShops || []).length} store${(p.publishShops || []).length !== 1 ? 's' : ''}`}
                    </div>
                  </div>
                  <button type="button" className="btn-height" style={s.unpubBtn} onClick={() => unpublish(p._id)}>Unpublish</button>
                </div>
              ))
            )}
          </div>
        </div>
      </>
  );

  if (embedded) {
    return <div style={s.embedded}>{inner}</div>;
  }

  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={s.modal}>{inner}</div>
    </div>
  );
}
